import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DestroyBase } from '../../../components/common/ng-destroy-base';
import { LocalizationNavigationService } from '../../../modules/app-settings/services/localization-navigation-service';
import { AdminTabs } from '../../enum';
import { NavigationService } from '../../navigation.service';
import { LocalizationService } from 'src/app/modules/app-settings/services/localization-service';
import { Router } from '@angular/router';
import { LocalizationModel } from '../../../modules/app-settings/models/localization-model';
import { TranslateService } from '@ngx-translate/core';
import { GenericPolluxResult } from '../../../models/generic-pollux-result';
import { EventBusService } from '../../../services/event-bus.service';

/**
 * Localization navigation component
 */
@Component({
  selector: 'localization-navigation',
  templateUrl: './localization-navigation.component.html',
  styleUrls: ['./localization-navigation.component.css']
})
export class LocalizationNavigationComponent extends DestroyBase implements OnInit {

  /**
  * Flag indicating whether the localization form is opened
  */
  isLocalizationFormOpen = false;

  /**
   * Selected Admin Tab
   */
  selectedTab: AdminTabs = AdminTabs.LocalizationHost;

  /**
  * Flag indicating whether the back to report designer icon should be displayed
  */
  displayBackToReportDesigner = false;

  /**
   * Indicate selected row on grid
   */
  isRowOnGridSelected = false;

  /**
   * Selected row on grid
   */
  selectedRow: LocalizationModel;

  /**
   * Display add and edit icons flag
   */
  displayAddEditIcons = true;

  /**
   * Constructor
   * @param navigationService
   * @param eventBus
   * @param _ref
   * @param localizationNavigationService
   * @param localizationService
   * @param router
   * @param translate
   */
  constructor(private navigationService: NavigationService,
    private eventBus: EventBusService,
    private _ref: ChangeDetectorRef,
    private localizationNavigationService: LocalizationNavigationService,
    private localizationService: LocalizationService,
    private router: Router,
    private translate: TranslateService) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.subscriptions.add(this.localizationNavigationService._templateSelectedSubject.subscribe(value => {
      this.displayBackToReportDesigner = value;
      if (this.displayBackToReportDesigner) {
        this.selectedTab = AdminTabs.LocalizationForm;
        this.navigationService.setActiveAdminTab(AdminTabs.LocalizationForm);
      }
      this._ref.markForCheck();
    }));

    this.subscriptions.add(this.localizationNavigationService._gridItemSelected.subscribe(data => {
      this.selectedRow = data;
      this.isRowOnGridSelected = !!data;//to boolean
      this._ref.markForCheck();
    }));

    this.subscriptions.add(this.localizationNavigationService._isTranslationUpdated.subscribe(data => {
      this.isLocalizationFormOpen = data;
      this._ref.markForCheck();
    }));

    this.subscriptions.add(this.localizationNavigationService._displayAddEditIcons.subscribe(data => {
      this.displayAddEditIcons = data;
      this._ref.markForCheck();
    }));
  }

  /**
  * On cancel action
  */
  onCancel() {
    this.localizationNavigationService.displaySaveLocalizationIcon(false);
  }

  /**
   * On save action
   */
  onSaveClicked() {
    this.localizationNavigationService.onSaveClicked();
  }

  /**
   * Redirects to the specified tab in the admin section.
   * @param tab The tab to redirect to.
   */
  redirectTo(tab: string) {
    this.setSelectedTab(tab);
    if (tab === 'localizationHost') this.localizationNavigationService.displayAddEditIcons(true);
    else this.localizationNavigationService.displayAddEditIcons(false);
    this.localizationNavigationService.onTemplateSelected(false);
    this.eventBus.broadcastEvent('adminTabChanged', tab);

    // You can add further comments or explanations if needed.
  }

  /**
  * Sets the selected tab based on the specified tab name.
   * @param tab The tab name.
   */
  private setSelectedTab(tab: string) {
    switch (tab) {
      case 'buManagement':
        this.selectedTab = AdminTabs.BusinessUnits;
        break;
      case 'userManagement':
        this.selectedTab = AdminTabs.Users;
        break;
      case 'managementroleManagement':
        this.selectedTab = AdminTabs.RoleManagment;
        break;
      case 'calcModels':
        this.selectedTab = AdminTabs.CalculationModel;
        break;
      case 'raciTemplate':
        this.selectedTab = AdminTabs.RaciTemplate;
        break;
      case 'localizationHost':
        this.selectedTab = AdminTabs.LocalizationHost;
        break;
      case 'localization':
        this.selectedTab = AdminTabs.LocalizationForm;
        break;
    }

    this.navigationService.setActiveAdminTab(this.selectedTab);

    // You can add further comments or explanations if needed.
  }

  /**
   * Returns the admin tabs enumeration.
   */
  public get adminTabs(): typeof AdminTabs {
    return AdminTabs;
  }

  /**
   * Open the new localization form
   */
  onOpenNewLocalizationForm() {
    this.localizationService.openForm(-1);
    this.localizationNavigationService.displayAddEditIcons(false);
  }

  /**
   * Returns to report designer page
   */
  backToReportDesigner() {
    const selectedLocalization = this.localizationNavigationService.getActiveLocalization();
    const selectedTemplate = this.localizationNavigationService.getActiveTemplate();
    sessionStorage.setItem('template', JSON.stringify(selectedTemplate));
    sessionStorage.setItem('localization', JSON.stringify(selectedLocalization));
    this.router.navigate(['/rd']);
  }

  /**
   * Delete language click handler
   */
  onDelete() {
    if (this.selectedRow) {
      if (this.selectedRow.isDefault) {
        //default language can't be deleted
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            // Do nothing
          }
        }, 'localizationNavigation.cantDeleteDefaultLocalization');
      } else {
        const id = this.selectedRow.id;
        const description = this.selectedRow.description;
        const message = this.translate.instant('localizationNavigation.deleteLocalization.message') + description;
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            this.localizationService.deleteLocalization(id).subscribe((result: GenericPolluxResult) => {
              if (result.success) {
                //success => refresh grid
                this.localizationService.refreshGrid();
              } else {
                //inform user about error
                this.showDeleteErrorMessage();
              }
            });
          },
          cancel: () => {
            // Do nothing
          },
          customMessage: () => {
            return message;
          }
        }, 'localizationNavigation.deleteLocalization');
      }
    }
  }

  /**
   * Show an error message if deleting failed
   */
  private showDeleteErrorMessage() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        // Do nothing
      }
    }, 'localizationNavigation.deleteLocalizationFailed');
  }
}
